// Globals
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { FormItem, IFormInstance, useWatchForm } from '@/components';
import { AttentionIcon, CheckmarkIcon } from '@/icons';
import { Input, Radio, RadioGroup } from '@/ui';
import MetaTagsTabs from '../MetaTagsTabs/MetaTagsTabs';
import { FormColumn, StyledButton, StyledWrapper } from '../Styled';
import { StyledForm, StyledInstructionBlock, StyledLink } from './MetaTagsFormStyles';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Models
import { IMetaTag } from '@/models/metaTags.model';

// Helpers
import { checkPath, META_TAGS_INSTRUCTION_LINK, UNDELETED_SLUGS } from '../../helpers/constants';
import { mapInitialFormValues } from '../../helpers/getInitialFormValues';
import { MetaTagsFormFieldsNames, TMetaTagsFormValues } from '../../helpers/types';
import { useMetaTagsFormRules, useSeoValidationRules } from '../../hooks';

interface IMetaTagForm {
  onSubmit: (values: TMetaTagsFormValues) => void;
  isLoading: boolean;
  initialValues?: IMetaTag | null;
  formInstance?: IFormInstance;
}

const MetaTagForm = ({ onSubmit, isLoading, initialValues, formInstance }: IMetaTagForm) => {
  const { t } = useTranslation(['metatags', 'common']);
  const { canManage } = usePermissions();
  const rules = useMetaTagsFormRules();
  const seoRules = useSeoValidationRules();

  const placeholder = t('common_placeholder_input', { ns: 'common' });
  const slug = useWatchForm<string>(MetaTagsFormFieldsNames.SLUG, formInstance);
  const initialFormValues = useMemo(() => mapInitialFormValues(initialValues), [initialValues]);

  return (
    <StyledWrapper>
      <StyledForm onFinish={onSubmit} clearOnDestroy initialValues={initialFormValues} form={formInstance}>
        <FormColumn>
          <FormItem
            name={MetaTagsFormFieldsNames.SLUG}
            label={t('metatags_slug_label')}
            rules={rules[MetaTagsFormFieldsNames.SLUG]}
          >
            <Input
              placeholder={placeholder}
              size="large"
              disabled={UNDELETED_SLUGS.includes(initialFormValues[MetaTagsFormFieldsNames.SLUG])}
            />
          </FormItem>
          <FormItem
            name={MetaTagsFormFieldsNames.INDEX}
            label={t('metatags_index_label')}
            rules={rules[MetaTagsFormFieldsNames.INDEX]}
          >
            <RadioGroup>
              <Radio value={true}>{t('metatags_index')}</Radio>
              <Radio value={false}>{t('metatags_noindex')}</Radio>
            </RadioGroup>
          </FormItem>
          <MetaTagsTabs hint={checkPath(slug)} rules={seoRules} />
          <StyledInstructionBlock>
            <AttentionIcon />
            <span>
              {t('metatags_instruction_text_label')}
              <StyledLink href={META_TAGS_INSTRUCTION_LINK} target="_blank" rel="noreferrer">
                {t('metatags_import_instruction_label')}
              </StyledLink>
            </span>
          </StyledInstructionBlock>
        </FormColumn>
        {canManage(Resource.SEO) && (
          <StyledButton htmlType="submit" loading={isLoading}>
            <CheckmarkIcon /> {t('metatags_save')}
          </StyledButton>
        )}
      </StyledForm>
    </StyledWrapper>
  );
};

export default MetaTagForm;
